import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Skeleton,
  CardContent,
  Paper,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import { useGetLoginTrackingQuery } from "api/homeApi";
import { reportError } from "utils/reportError";
import ContentUnavailable from "components/ContentUnavailable";
import BusyLoader from "components/BusyLoader";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  STAT_CARD_COLORS,
  STATUS_COLORS,
  TABLE_CONFIG,
  CHIP_STYLES,
  TABLE_HEADER_STYLE,
  formatLoginDate,
  formatEventDateTime,
  calculateLoginPercentage,
} from "utils/loginTrackingConfig";

// Styled components matching Dashboard style
const DrawerHeader = styled("Box")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme?.spacing ? theme.spacing(0, 1) : "0 8px",
  ...theme?.mixins?.toolbar,
}));

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "20px",
  color: "#0a2240",
  marginTop: "1rem",
  marginBottom: "1rem",
});

const SectionTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "1.1rem",
  color: "#0a2240",
  marginBottom: "1rem",
});

const StyledCard = styled(Card)({
  backgroundColor: "#ffffff",
  borderRadius: "14px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  height: "100%",
  "& .MuiCardContent-root": {
    padding: "20px",
  },
});

const StatCard = styled(Card)(({ bgcolor }) => ({
  backgroundColor: bgcolor || "#f8f9fa",
  borderRadius: "14px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  height: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-2px)",
  },
  "& .MuiCardContent-root": {
    padding: "20px",
  },
}));

const StatValue = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "2rem",
  color: "#0a2240",
});

const StatLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: "0.9rem",
  color: "#666",
  marginTop: "0.5rem",
});

const StyledChip = styled(Chip)({
  fontFamily: "Montserrat",
  fontWeight: 600,
});

const StyledTableCell = styled(TableCell)({
  fontFamily: "Montserrat",
});

// Loading skeleton component
const LoadingSkeleton = () => (
  <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" } }}>
    <Stack spacing={3}>
      <Skeleton variant="rectangular" height={150} sx={{ borderRadius: "14px" }} />
      <Grid container spacing={3}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item}>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "14px" }} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rectangular" height={300} sx={{ borderRadius: "14px" }} />
    </Stack>
  </Box>
);

export const LoginTracking = ({ user, features }) => {
  const { data: apiData, isLoading, isError, error } = useGetLoginTrackingQuery();
  const errorReported = useRef(false);

  useEffect(() => {
    if (!isLoading && !isError && !apiData && !errorReported.current) {
      errorReported.current = true;
      reportError({ page: "Login Tracking", detail: "API returned no data from /getLoginTracking/" });
    }
  }, [isLoading, isError, apiData]);

  if (isError) {
    return (
      <Box sx={{ padding: { xs: "0.5rem", sm: "1.3vw" } }}>
        <DrawerHeader />
        <Alert severity="error" sx={{ borderRadius: "14px" }}>
          Error loading data: {error?.message || "Unknown error"}
        </Alert>
      </Box>
    );
  }

  if (!isLoading && !apiData) {
    return (
      <Box sx={{ padding: { xs: "0.5rem", sm: "1.3vw" } }}>
        <DrawerHeader />
        <ContentUnavailable page="Login Tracking" />
      </Box>
    );
  }

  const { summary, daily_logins, top_users, recent_failures, recent_successes } = apiData || {};

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: "#F4F7FE",
        minHeight: "100vh",
        padding: { xs: "0.5rem", sm: "0" },
      }}
    >
      <DrawerHeader />
      <BusyLoader open={isLoading} />

      <PageTitle sx={{ paddingLeft: { xs: "0.3rem", sm: "1.3vw" } }}>
        Login Tracking Dashboard
      </PageTitle>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" }, paddingTop: 0 }}>
          {/* Summary Cards Row 1 - Total Stats */}
          <Grid container spacing={3} sx={{ marginBottom: "2rem" }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard bgcolor={STAT_CARD_COLORS.totalLogins.bg}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <PersonIcon sx={{ fontSize: 40, color: STAT_CARD_COLORS.totalLogins.icon }} />
                    <Box>
                      <StatValue>{parseInt(summary?.total?.logins || 0).toLocaleString()}</StatValue>
                      <StatLabel>Total Logins</StatLabel>
                    </Box>
                  </Stack>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StatCard bgcolor={STAT_CARD_COLORS.successfulLogins.bg}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: STAT_CARD_COLORS.successfulLogins.icon }} />
                    <Box>
                      <StatValue>{parseInt(summary?.total?.success || 0).toLocaleString()}</StatValue>
                      <StatLabel>Successful Logins</StatLabel>
                    </Box>
                  </Stack>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StatCard bgcolor={STAT_CARD_COLORS.failedLogins.bg}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CancelIcon sx={{ fontSize: 40, color: STAT_CARD_COLORS.failedLogins.icon }} />
                    <Box>
                      <StatValue>{parseInt(summary?.total?.failures || 0).toLocaleString()}</StatValue>
                      <StatLabel>Failed Logins</StatLabel>
                    </Box>
                  </Stack>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          {/* Summary Cards Row 2 - Time-based Stats */}
          <Grid container spacing={3} sx={{ marginBottom: "2rem" }}>
            <Grid item xs={12} md={6}>
              <StatCard bgcolor={STAT_CARD_COLORS.last7Days.bg}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CalendarTodayIcon sx={{ fontSize: 40, color: STAT_CARD_COLORS.last7Days.icon }} />
                    <Box sx={{ width: "100%" }}>
                      <SectionTitle sx={{ marginBottom: "1rem" }}>Last 7 Days</SectionTitle>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#0a2240" }}>
                            {parseInt(summary?.last_7_days?.logins || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Total</StatLabel>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, color: STATUS_COLORS.success }}>
                            {parseInt(summary?.last_7_days?.success || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Successful</StatLabel>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, color: STATUS_COLORS.failure }}>
                            {parseInt(summary?.last_7_days?.failures || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Failed</StatLabel>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <StatCard bgcolor={STAT_CARD_COLORS.last3Days.bg}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: STAT_CARD_COLORS.last3Days.icon }} />
                    <Box sx={{ width: "100%" }}>
                      <SectionTitle sx={{ marginBottom: "1rem" }}>Last 3 Days</SectionTitle>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#0a2240" }}>
                            {parseInt(summary?.last_3_days?.logins || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Total</StatLabel>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, color: STATUS_COLORS.success }}>
                            {parseInt(summary?.last_3_days?.success || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Successful</StatLabel>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, color: STATUS_COLORS.failure }}>
                            {parseInt(summary?.last_3_days?.failures || 0).toLocaleString()}
                          </Typography>
                          <StatLabel>Failed</StatLabel>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          {/* Daily Logins Table */}
          <StyledCard sx={{ marginBottom: "2rem" }}>
            <CardContent>
              <SectionTitle>Daily Logins</SectionTitle>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell sx={TABLE_HEADER_STYLE}>Date</StyledTableCell>
                      <StyledTableCell sx={TABLE_HEADER_STYLE} align="right">
                        Login Count
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daily_logins?.slice(0, TABLE_CONFIG.dailyLoginsLimit).map((day, index) => (
                      <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <StyledTableCell>
                          <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600 }}>
                            {formatLoginDate(day.date)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <StyledChip
                            label={day.count.toLocaleString()}
                            size="medium"
                            sx={CHIP_STYLES.dailyCount}
                          />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>

          {/* Top Users */}
          <StyledCard sx={{ marginBottom: "2rem" }}>
            <CardContent>
              <SectionTitle>Top {TABLE_CONFIG.topUsersLimit} Users by Login Count</SectionTitle>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell sx={TABLE_HEADER_STYLE}>Position</StyledTableCell>
                      <StyledTableCell sx={TABLE_HEADER_STYLE}>Username</StyledTableCell>
                      <StyledTableCell sx={TABLE_HEADER_STYLE} align="right">
                        Login Count
                      </StyledTableCell>
                      <StyledTableCell sx={TABLE_HEADER_STYLE} align="right">
                        Percentage
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top_users?.map((topUser, index) => {
                      const percentage = calculateLoginPercentage(topUser.login_count, parseInt(summary?.total?.logins || 1));
                      return (
                        <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                          <StyledTableCell>
                            <StyledChip
                              label={`#${index + 1}`}
                              size="small"
                              sx={CHIP_STYLES.topPosition(index)}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600 }}>
                              {topUser.username}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, color: STATUS_COLORS.secondary }}>
                              {topUser.login_count.toLocaleString()}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={parseFloat(percentage)}
                                sx={{
                                  width: 100,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "#E0E0E0",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: STATUS_COLORS.secondary,
                                  },
                                }}
                              />
                              <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.9rem", minWidth: "45px" }}>
                                {percentage}%
                              </Typography>
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>

          {/* Recent Failures and Successes */}
          <Grid container spacing={3}>
            {/* Recent Failures */}
            <Grid item xs={12} lg={6}>
              <StyledCard>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: "1rem" }}>
                    <WarningIcon sx={{ color: STATUS_COLORS.failure }} />
                    <SectionTitle sx={{ marginBottom: 0 }}>Recent Failed Logins</SectionTitle>
                  </Stack>
                  <TableContainer component={Paper} elevation={0} sx={{ maxHeight: TABLE_CONFIG.maxTableHeight }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Username</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Date/Time</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Error</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>IP</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recent_failures?.slice(0, TABLE_CONFIG.recentEventsLimit).map((failure) => (
                          <TableRow key={failure.id} sx={{ "&:hover": { backgroundColor: STAT_CARD_COLORS.failedLogins.bg } }}>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, fontSize: "0.85rem" }}>
                                {failure.username}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.8rem", color: STATUS_COLORS.neutral }}>
                                {formatEventDateTime(failure.event_time)}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <StyledChip
                                label={failure.error}
                                size="small"
                                sx={CHIP_STYLES.failureError}
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.8rem", color: STATUS_COLORS.neutral }}>
                                {failure.ip_address}
                              </Typography>
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Recent Successes */}
            <Grid item xs={12} lg={6}>
              <StyledCard>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: "1rem" }}>
                    <CheckCircleIcon sx={{ color: STATUS_COLORS.success }} />
                    <SectionTitle sx={{ marginBottom: 0 }}>Recent Successful Logins</SectionTitle>
                  </Stack>
                  <TableContainer component={Paper} elevation={0} sx={{ maxHeight: TABLE_CONFIG.maxTableHeight }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Username</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Date/Time</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>Client</StyledTableCell>
                          <StyledTableCell sx={TABLE_HEADER_STYLE}>IP</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recent_successes?.slice(0, TABLE_CONFIG.recentEventsLimit).map((success) => (
                          <TableRow key={success.id} sx={{ "&:hover": { backgroundColor: STAT_CARD_COLORS.successfulLogins.bg } }}>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontWeight: 600, fontSize: "0.85rem" }}>
                                {success.username}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.8rem", color: STATUS_COLORS.neutral }}>
                                {formatEventDateTime(success.event_time)}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <StyledChip
                                label={success.client_id}
                                size="small"
                                sx={CHIP_STYLES.successClient}
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.8rem", color: STATUS_COLORS.neutral }}>
                                {success.ip_address}
                              </Typography>
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default LoginTracking;
